/**
 * Cart Gift Validator
 * Ensures only one gift product is in the cart at a time
 */

(function() {
  'use strict';

  // Configuration
  const GIFT_IDS = {
    gift1: 57343130960253,
    gift2: 57342806524285,
  };

  const ALL_GIFT_IDS = Object.values(GIFT_IDS);
  
  const STORAGE = {
    SELECTED: 'selectedGiftId',
    JUST_MUTATED: 'giftJustMutated'
  };

  const MIN_INTERVAL = 800; // ms between validations
  
  // State
  let running = false;
  let lastRun = 0;
  let opQueue = Promise.resolve();

  /**
   * Check if variant ID is a gift
   */
  function isGift(variantId) {
    return ALL_GIFT_IDS.includes(variantId);
  }

  /**
   * Get real products (non-gifts)
   */
  function getRealProducts(items) {
    return items.filter(item => !isGift(item.id));
  }

  /**
   * Get gift products
   */
  function getGiftsInCart(items) {
    return items.filter(item => isGift(item.id));
  }

  /**
   * Fetch JSON with error handling
   */
  async function fetchJSON(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      ...options
    });
    
    if (!response.ok) {
      throw new Error(`${options.method || 'GET'} ${url} failed (${response.status})`);
    }
    
    return response.json();
  }

  /**
   * Get current cart
   */
  async function getCart() {
    return fetchJSON('/cart.js');
  }

  /**
   * Change line item quantity
   */
  async function changeLineQtyByKey(lineKey, qty) {
    return fetchJSON('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: qty }),
    });
  }

  /**
   * Queue operations to prevent race conditions
   */
  function enqueue(fn) {
    opQueue = opQueue.then(fn).catch(console.error);
    return opQueue;
  }

  /**
   * Choose which gift to keep in cart
   */
  function chooseGiftToKeep(giftsInCart, savedGiftId) {
    if (savedGiftId) {
      const match = giftsInCart.find(g => String(g.id) === String(savedGiftId));
      if (match) return match;
    }
    return giftsInCart[0] || null;
  }

  /**
   * Main validation logic
   */
  async function validateGiftInCart() {
    try {
      // Skip if on checkout page
      if (location.pathname.startsWith('/checkout')) {
        return;
      }

      // Throttle execution
      const now = Date.now();
      if (now - lastRun < MIN_INTERVAL || running) {
        return;
      }
      
      running = true;
      lastRun = now;

      // Skip if we just mutated the cart
      if (sessionStorage.getItem(STORAGE.JUST_MUTATED)) {
        sessionStorage.removeItem(STORAGE.JUST_MUTATED);
        running = false;
        return;
      }

      // Get current cart
      const cart = await getCart();
      const items = cart.items || [];

      // Empty cart - clear saved selection
      if (items.length === 0) {
        localStorage.removeItem(STORAGE.SELECTED);
        running = false;
        return;
      }

      const realProducts = getRealProducts(items);
      const gifts = getGiftsInCart(items);

      // Case A: Only gifts (no real products) - remove all gifts
      if (realProducts.length === 0 && gifts.length > 0) {
        await enqueue(async () => {
          for (const gift of gifts) {
            await changeLineQtyByKey(gift.key, 0);
          }
        });
        
        localStorage.removeItem(STORAGE.SELECTED);
        sessionStorage.setItem(STORAGE.JUST_MUTATED, '1');
        location.reload();
        return;
      }

      // Case B: Has real products - normalize gifts
      if (realProducts.length > 0) {
        let mutated = false;
        const savedGiftId = localStorage.getItem(STORAGE.SELECTED);

        if (gifts.length === 0) {
          // No gifts - clean up saved selection
          if (savedGiftId) {
            localStorage.removeItem(STORAGE.SELECTED);
          }
          running = false;
          return;
        }

        // B1: Normalize gift quantities to 1
        await enqueue(async () => {
          for (const gift of gifts) {
            if (gift.quantity > 1) {
              await changeLineQtyByKey(gift.key, 1);
              mutated = true;
            }
          }
        });

        // B2: Multiple gifts - keep only one
        if (gifts.length > 1) {
          const toKeep = chooseGiftToKeep(gifts, savedGiftId);
          const toRemove = gifts.filter(g => g.key !== toKeep.key);

          await enqueue(async () => {
            for (const gift of toRemove) {
              await changeLineQtyByKey(gift.key, 0);
              mutated = true;
            }
          });

          localStorage.setItem(STORAGE.SELECTED, String(toKeep.id));
        } else {
          // B3: Exactly one gift - save as preferred
          const onlyGift = gifts[0];
          if (String(savedGiftId) !== String(onlyGift.id)) {
            localStorage.setItem(STORAGE.SELECTED, String(onlyGift.id));
          }
        }

        // Reload if cart was mutated
        if (mutated) {
          sessionStorage.setItem(STORAGE.JUST_MUTATED, '1');
          location.reload();
          return;
        }
      }

    } catch (error) {
      console.error('Gift validation error:', error);
    } finally {
      running = false;
    }
  }

  /**
   * Debounce helper
   */
  function debounce(fn, wait = 200) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), wait);
    };
  }

  /**
   * Initialize gift validation
   */
  function init() {
    // Prevent duplicate initialization
    if (window.__giftValidationInitialized) {
      return;
    }
    window.__giftValidationInitialized = true;

    const validateDebounced = debounce(validateGiftInCart, 200);

    // Initial validation
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', validateDebounced);
    } else {
      validateDebounced();
    }

    // Listen to cart events
    document.addEventListener('cart:updated', validateDebounced);
    document.addEventListener('cart.requestComplete', validateDebounced);

    // Validate when tab becomes visible
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        validateDebounced();
      }
    });

    // Periodic validation (moderate polling)
    setInterval(() => {
      if (!document.hidden) {
        validateDebounced();
      }
    }, 2000);
  }

  // Auto-initialize
  init();

})();
